import React from 'react'
// libs
import { change } from 'redux-form'
import { useDispatch, useSelector } from 'react-redux'
// components
import { AddButton } from 'themes/views/security'
import { CloseIcon } from 'components/Modals/GroupSecurityModal/Form/BusinessRule/views'
import { ReduxSelect } from 'components/FormElements'
import Lookup from 'components/PageComponents/RiskTemplates/Edit/components/RiskTemplateFields/components/Lookup'
// constants
import { Forms } from 'constants/index'
// store
import { responseTypesSelector } from 'store/reducers/riskTemplates'
// types
import { FieldType, IField } from 'types/graphql'
import { WrappedFieldArrayProps } from 'redux-form/lib/FieldArray'
// views
import { FieldWrapper } from './views'
// utils
import pipe from 'lodash/fp/pipe'
import find from 'lodash/fp/find'
import { required } from 'utils/validators'
import { getValueField } from 'components/Modals/RiskViewFilterModal/utils'

const FIELD_TYPES_TO_EXCLUDE = [
  FieldType.RiskMatrix,
  FieldType.Table,
  FieldType.Label,
  FieldType.DocumentOrLink,
  FieldType.Tag
]

interface Props {
  lookupProps?: React.ComponentProps<typeof Lookup>
  templateFields: IField[]
  dueDateNowOnly?: boolean
}

const MappedProperty: React.FC<Props & WrappedFieldArrayProps<any>> = ({
  fields,
  lookupProps,
  templateFields,
  dueDateNowOnly
}) => {
  const dispatch = useDispatch()

  const responseTypeSets = useSelector(responseTypesSelector)

  const addField = () => fields.push({})

  const removeField = (idx: number) => () => fields.remove(idx)

  const selectedFields: string[] = fields.getAll()?.map((item) => item.field?.value) || []

  const options = templateFields
    .filter((item) => !FIELD_TYPES_TO_EXCLUDE.includes(item.fieldType) && !selectedFields.includes(item.id))
    .map((item) => ({
      label: item.parentFieldId
        ? `${templateFields.find((f) => f.id === item.parentFieldId)?.label}: ${item.label}`
        : item.label,
      value: item.id,
      field: item
    }))

  const onFieldChange = (name: string) => () => {
    dispatch(change(Forms.SYSTEM_PROCESS_CREATE_FORM, name, null))
  }

  return (
    <div>
      {fields.map((name, idx) => {
        const mappedProperty = fields.get(idx)

        const field = pipe(
          find((item: IField) => item.id === mappedProperty?.field?.value),
          (item) => {
            if (item && item.fieldType === FieldType.PickList) {
              return { ...item, options: item.options.map((option) => ({ label: option.label, value: option.id })) }
            }

            return item
          }
        )(templateFields)

        return (
          <FieldWrapper key={name}>
            <ReduxSelect
              options={options}
              validate={required}
              name={`${name}.field`}
              menuPortalTarget={null}
              onSelectChange={onFieldChange(`${name}.value`)}
            />
            {getValueField({
              field,
              name: `${name}.value`,
              scheduled: true,
              lookupProps: { menuPortalTarget: null, ...lookupProps },
              dueDateNowOnly,
              responseTypeSets
            })}
            <CloseIcon data-testid='mapped-property-remove' data-entity-id={idx} onClick={removeField(idx)} />
          </FieldWrapper>
        )
      })}
      <AddButton data-testid='mapped-property-add' onClick={addField}>
        + add property
      </AddButton>
    </div>
  )
}
export default MappedProperty
